import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Paper,
  Chip,
  ButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import { OfficeViewerProps, ViewerError } from '../../../types/viewer.types';
import { isFileTooLargeError, extractServerMessage } from '../../../utils/conversion-error';
import PDFViewer from './PDFViewer';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService, uploadDirectService } from '../../../services';

interface ConversionStatus {
  status: 'idle' | 'converting' | 'completed' | 'failed' | 'too-large';
  progress?: number;
  error?: string;
  convertedUrl?: string;
}

// File size thresholds
const CONVERSION_MAX_SIZE = 500 * 1024 * 1024; // 500MB - maximum size for PDF conversion

/**
 * Office Document Viewer - Converts Office docs to PDF for viewing
 * Supports Word, Excel, PowerPoint, and OpenDocument formats
 */
const OfficeViewer: React.FC<OfficeViewerProps> = ({
  contentId,
  teamId,
  metadata,
  onError,
  onLoad,
  className,
  style,
  publicLinkCredentials,
}) => {
  const [conversionStatus, setConversionStatus] = useState<ConversionStatus>({ status: 'idle' });
  const [pdfMetadata, setPdfMetadata] = useState<any>(null);
  const conversionTimeoutRef = useRef<NodeJS.Timeout>();
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const conversionRequestRef = useRef<AbortController | null>(null);
  const isConvertingRef = useRef<boolean>(false);

  useEffect(() => {
    // Prevent duplicate conversion requests
    if (isConvertingRef.current) {
      return;
    }

    // Create abort controller for this conversion request
    const abortController = new AbortController();
    conversionRequestRef.current = abortController;

    // Decide viewing strategy based on file size
    if (conversionStatus.status === 'idle') {
      // Parse fileSize - it might be a string or number
      let fileSize = 0;
      if (typeof metadata.fileSize === 'number') {
        fileSize = metadata.fileSize;
      } else if (typeof metadata.fileSize === 'string') {
        fileSize = parseInt(metadata.fileSize, 10) || 0;
      }

      const fileSizeMB = Math.round(fileSize / (1024 * 1024));
      const isPowerPoint = metadata.mimeType?.includes('presentation') || metadata.mimeType?.includes('powerpoint');

      console.log('Office viewer file size check:', {
        fileSize,
        fileSizeMB,
        conversionMaxSizeMB: Math.round(CONVERSION_MAX_SIZE / (1024 * 1024)),
      });

      if (fileSize > CONVERSION_MAX_SIZE) {
        // Very large file (>500MB) - download only
        console.log('File too large for conversion, showing download prompt');
        setConversionStatus({
          status: 'too-large',
          error: `This file is ${fileSizeMB}MB, which is too large to preview online. Please download it to view.`
        });
        isConvertingRef.current = false;
      } else {
        // File ≤500MB - convert to PDF for viewing
        console.log(`Converting file to PDF (${fileSizeMB}MB)`);
        startConversion();
      }
    }

    return () => {
      // Cleanup: abort any pending requests
      if (conversionRequestRef.current) {
        conversionRequestRef.current.abort();
        conversionRequestRef.current = null;
      }

      if (conversionTimeoutRef.current) {
        clearTimeout(conversionTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      isConvertingRef.current = false;
    };
  }, [contentId]);

  const getOfficeIcon = (mimeType: string): string => {
    if (mimeType.includes('word') || mimeType.includes('document')) return '📄';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
    if (mimeType.includes('opendocument')) return '📝';
    return '📄';
  };

  const getOfficeTypeName = (mimeType: string): string => {
    // Check PowerPoint/Excel first since they contain "document" in their MIME types
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'PowerPoint Presentation';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Excel Spreadsheet';
    if (mimeType.includes('word') || mimeType.includes('wordprocessing')) return 'Word Document';
    if (mimeType.includes('opendocument.presentation')) return 'OpenDocument Presentation';
    if (mimeType.includes('opendocument.spreadsheet')) return 'OpenDocument Spreadsheet';
    if (mimeType.includes('opendocument.text')) return 'OpenDocument Text';
    if (mimeType.includes('rtf')) return 'Rich Text Document';
    return 'Office Document';
  };

  const startConversion = async () => {
    // Prevent duplicate conversions
    if (isConvertingRef.current) {
      console.log('Conversion already in progress, skipping duplicate request');
      return;
    }
    
    try {
      isConvertingRef.current = true;
      setConversionStatus({ status: 'converting', progress: 0 });

      // Validate teamId is present
      if (!teamId) {
        throw new Error('Team ID is required for content conversion');
      }

      // Request conversion using API service
      // Pass publicLinkCredentials if available (for public link access)
      const result = await contentService.convertContentToPdf(contentId, teamId, publicLinkCredentials);
      
      // Check if conversion was immediate (PDF already available)
      if (result.immediate && result.pdfUrl) {
        console.log('Immediate PDF conversion detected:', {
          pdfUrl: result.pdfUrl,
          immediate: result.immediate,
          status: result.status
        });
        
        // Show a smooth progress animation for immediate conversions (better UX)
        let currentProgress = 0;
        const progressInterval = setInterval(() => {
          currentProgress += 15;
          if (currentProgress >= 100) {
            currentProgress = 100;
            clearInterval(progressInterval);
            
            // Conversion completed - PDF is ready
            setConversionStatus({ 
              status: 'completed', 
              convertedUrl: result.pdfUrl,
              progress: 100 
            });
            
            // Set PDF metadata for immediate conversion
            setPdfMetadata({
              ...metadata,
              contentType: 'document',
              mimeType: 'application/pdf',
              canConvert: false,
            });
            
            console.log('PDF metadata set for immediate conversion');
            isConvertingRef.current = false;
          } else {
            setConversionStatus({ 
              status: 'converting', 
              progress: currentProgress 
            });
          }
        }, 200); // Update every 200ms for smooth animation
      } else if (result.jobId) {
        // Conversion is async - start polling with the job ID
        startPollingConversion(result.jobId);
      } else if (result.status === 'failed') {
        // Conversion failed immediately
        setConversionStatus({
          status: 'failed',
          error: result.message || 'Conversion failed',
        });
        isConvertingRef.current = false;
      } else {
        // Fallback - if we have a jobId, poll with it
        if (result.jobId) {
          startPollingConversion(result.jobId);
        } else {
          // No job ID and not immediate - this is an error
          setConversionStatus({
            status: 'failed',
            error: 'Invalid conversion response - no job ID or immediate result',
          });
          isConvertingRef.current = false;
        }
      }
    } catch (error) {
      // A file above the preview size cap is a deliberate, permanent decision by
      // the content service — not a failure. Surface the server's own
      // explanation and do not offer Retry; retrying can never succeed.
      const serverMessage = extractServerMessage(error);

      // A refusal on size is a deliberate, permanent product decision, not a
      // failure. Route it to the existing 'too-large' screen — which already
      // reads as information and offers a Download button — instead of the red
      // error surface. Calling onError here would replace that screen with
      // "An unexpected error occurred", which is what the user reported.
      if (isFileTooLargeError(error)) {
        console.info('Office viewer: preview declined, file above the server size cap');
        setConversionStatus({
          status: 'too-large',
          error: serverMessage,
        });
        isConvertingRef.current = false;
        return;
      }

      console.error('Office viewer conversion error:', error);

      setConversionStatus({
        status: 'failed',
        error: serverMessage || (error instanceof Error ? error.message : 'Failed to start conversion'),
      });

      const viewerError: ViewerError = {
        code: 'CONVERSION_ERROR',
        message: serverMessage || 'Failed to convert document to PDF',
        details: error,
        retryable: true,
      };

      // Wrap onError call in try-catch to prevent uncaught errors
      if (onError) {
        try {
          onError(viewerError);
        } catch (callbackError) {
          console.error('Error in onError callback:', callbackError);
        }
      }

      isConvertingRef.current = false;
    }
  };

  const startPollingConversion = (jobId: string) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    
    const pollStatus = async () => {
      try {
        attempts++;
        
        const status = await contentService.getConversionStatus(contentId, jobId, publicLinkCredentials);
        
        switch (status.status) {
          case 'completed':
            setConversionStatus({
              status: 'completed',
              progress: 100,
              convertedUrl: status.pdfUrl || '',
            });
            
            setPdfMetadata({
              ...metadata,
              contentType: 'document',
              mimeType: 'application/pdf',
              canConvert: false,
            });
            
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
            
            onLoad?.();
            isConvertingRef.current = false;
            break;
            
          case 'failed':
            setConversionStatus({
              status: 'failed',
              error: status.error || 'Conversion failed',
            });
            
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
            isConvertingRef.current = false;
            break;
            
          case 'processing':
            setConversionStatus({
              status: 'converting',
              progress: status.progress || Math.min((attempts / maxAttempts) * 100, 95),
            });
            break;
        }
        
        if (attempts >= maxAttempts) {
          setConversionStatus({
            status: 'failed',
            error: 'Conversion timed out',
          });
          
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          isConvertingRef.current = false;
        }
      } catch (error) {
        console.error('Error polling conversion status:', error);
        
        if (attempts >= 5) { // Give up after 5 failed attempts
          setConversionStatus({
            status: 'failed',
            error: 'Failed to check conversion status',
          });
          
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          isConvertingRef.current = false;
        }
      }
    };

    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(pollStatus, 5000);
    
    // Initial poll
    pollStatus();
  };

  const handleRetryConversion = () => {
    isConvertingRef.current = false;
    setConversionStatus({ status: 'idle' });
    startConversion();
  };

  const handleDownload = () => {
    const downloadUrl = `/content/${contentId}/download${teamId ? `?team_id=${teamId}` : ''}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = metadata.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // If file is too large for viewing, show download prompt
  if (conversionStatus.status === 'too-large') {
    // Parse fileSize properly (might be string or number)
    let fileSize = 0;
    if (typeof metadata.fileSize === 'number') {
      fileSize = metadata.fileSize;
    } else if (typeof metadata.fileSize === 'string') {
      fileSize = parseInt(metadata.fileSize, 10) || 0;
    }

    const fileSizeMB = Math.round(fileSize / (1024 * 1024));
    const fileSizeGB = (fileSizeMB / 1024).toFixed(2);

    return (
      <Box
        className={className}
        style={style}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          p: 4,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ fontSize: 64, mb: 2 }}>
            {getOfficeIcon(metadata.mimeType || '')}
          </Box>
          <Typography variant="h6" gutterBottom>
            {metadata.fileName}
          </Typography>
          <Chip
            label={fileSizeMB > 1024 ? `${fileSizeGB}GB` : `${fileSizeMB}MB`}
            color="warning"
            sx={{ mb: 2 }}
          />
        </Box>

        <Alert severity="info" sx={{ maxWidth: 600 }}>
          <Typography variant="body1" gutterBottom>
            <strong>File Too Large for Online Preview</strong>
          </Typography>
          <Typography variant="body2">
            {/* Prefer the server's own explanation: the effective preview limit
                lives in the content service (CONVERSION_MAX_FILE_BYTES) and is
                lower than this component's local 500MB guard, so hardcoding
                "500MB" here would state the wrong number. */}
            {conversionStatus.error
              ? conversionStatus.error
              : `This file is ${fileSizeMB > 1024 ? `${fileSizeGB}GB` : `${fileSizeMB}MB`}, which exceeds the maximum size for online viewing. Please download the file to view it with the appropriate application on your computer.`}
          </Typography>
        </Alert>

        <Button
          variant="contained"
          size="large"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ minWidth: 200 }}
        >
          Download File
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 500 }}>
          {/* No size claim here — the authoritative limit is the server's and is
              already stated in the alert above. */}
          {getOfficeTypeName(metadata.mimeType || '')} • Download to open in its native application
        </Typography>
      </Box>
    );
  }

  // If conversion is completed and we have PDF metadata, show PDF viewer
  if (conversionStatus.status === 'completed' && pdfMetadata && conversionStatus.convertedUrl) {
    console.log('Rendering PDFViewer with converted document:', {
      contentId,
      pdfMetadata,
      convertedUrl: conversionStatus.convertedUrl,
      isConverted: true
    });

    return (
      <PDFViewer
        contentId={contentId}
        teamId={teamId}
        metadata={pdfMetadata}
        onError={onError}
        onLoad={onLoad}
        className={className}
        style={style}
        isConverted={true}  // Tell PDFViewer this is a converted document
        convertedUrl={conversionStatus.convertedUrl}  // Pass the presigned URL
      />
    );
  }

  const officeIcon = getOfficeIcon(metadata.mimeType);
  const officeTypeName = getOfficeTypeName(metadata.mimeType);

  return (
    <Box
      className={className}
      style={style}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* File Icon and Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Box sx={{ fontSize: '4rem' }}>
            {conversionStatus.status === 'completed' ? '✅' : officeIcon}
          </Box>
          
          <Typography variant="h6" component="h2" gutterBottom>
            {metadata.fileName}
          </Typography>
          
          {conversionStatus.status === 'completed' && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip
                label="Converted to PDF"
                color="success"
                size="small"
                icon={<PdfIcon />}
              />
            </Box>
          )}
        </Box>

        {/* Conversion Status */}
        {conversionStatus.status === 'converting' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} color="primary" />
              <Typography variant="body1">
                Converting to PDF for viewing...
              </Typography>
            </Box>

            {conversionStatus.progress !== undefined && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={conversionStatus.progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {Math.round(conversionStatus.progress)}% complete
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              This may take a few moments depending on document size and complexity.
            </Typography>
          </Box>
        )}

        {conversionStatus.status === 'failed' && (
          <Alert
            severity="error"
            sx={{ textAlign: 'left' }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRetryConversion}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="subtitle2" gutterBottom>
              Conversion Failed
            </Typography>
            <Typography variant="body2">
              {conversionStatus.error || 'Unable to convert document to PDF for viewing.'}
            </Typography>
          </Alert>
        )}

        {conversionStatus.status === 'completed' && (
          <Alert severity="success" sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>
              Conversion Complete
            </Typography>
            <Typography variant="body2">
              Document has been converted to PDF and is ready for viewing.
            </Typography>
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            size="large"
          >
            Download Original
          </Button>
          
          {conversionStatus.status === 'failed' && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetryConversion}
              size="large"
            >
              Retry Conversion
            </Button>
          )}
        </Box>

        {/* Help Text */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          Office documents are converted to PDF for secure viewing in your browser.
        </Typography>
      </Paper>
    </Box>
  );
};

export default OfficeViewer;